import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import s from '../../pages/PedidosOrcamentos.module.css';

// Montagem do MDF-e (Etapa 3) — fluxo pedido: selecionar NF-e → sistema
// preenche origem/destino/peso/valor automaticamente → escolher veículo →
// escolher motorista → conferir percurso → validar → (Etapa 4+) transmitir.
// Ainda não transmite; só grava em 'digitacao'/'validado'.

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA',
  'PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function ModalNovoMdfe({ mdfeId, onClose, onSalvo }) {
  const [carregando, setCarregando] = useState(!!mdfeId);
  const [nfeDisponiveis, setNfeDisponiveis] = useState([]);
  const [selecionadas, setSelecionadas] = useState({}); // documento_fiscal_id -> documento
  const [veiculos, setVeiculos] = useState([]);
  const [condutoresLista, setCondutoresLista] = useState([]);
  const [municipiosCarregamento, setMunicipiosCarregamento] = useState([]);

  const [ufCarregamento, setUfCarregamento] = useState('');
  const [municipioCarregamento, setMunicipioCarregamento] = useState(''); // "nome|codigo"
  const [veiculoTracaoId, setVeiculoTracaoId] = useState('');
  const [reboquesIds, setReboquesIds] = useState([]);
  const [condutoresIds, setCondutoresIds] = useState([]);
  const [percurso, setPercurso] = useState([]); // [uf,...]
  const [percursoNovo, setPercursoNovo] = useState('');
  const [informacoesAdicionais, setInformacoesAdicionais] = useState('');

  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [resultadoValidacao, setResultadoValidacao] = useState(null);
  const [validando, setValidando] = useState(false);
  const [statusAtual, setStatusAtual] = useState('digitacao');

  // ── Carga inicial ──────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/fiscal/veiculos').then(r => setVeiculos(r.data || [])).catch(() => {});
    api.get('/fiscal/condutores').then(r => setCondutoresLista(r.data || [])).catch(() => {});
  }, []);

  const carregarNfeDisponiveis = useCallback(async () => {
    try {
      const { data } = await api.get('/fiscal/mdfe/nfe-disponiveis');
      setNfeDisponiveis(data || []);
    } catch { setErro('Erro ao carregar NF-e disponíveis.'); }
  }, []);
  useEffect(() => { carregarNfeDisponiveis(); }, [carregarNfeDisponiveis]);

  useEffect(() => {
    if (!ufCarregamento) { setMunicipiosCarregamento([]); return; }
    api.get('/fiscal/mdfe/municipios', { params: { uf: ufCarregamento } })
      .then(r => setMunicipiosCarregamento(r.data || [])).catch(() => setMunicipiosCarregamento([]));
  }, [ufCarregamento]);

  // Editando: carrega o MDF-e existente e preenche tudo, incluindo a NF-e já
  // vinculada (que não vem mais em nfe-disponiveis, mas precisa aparecer
  // marcada aqui).
  useEffect(() => {
    if (!mdfeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/fiscal/mdfe/${mdfeId}`);
        setStatusAtual(data.status);
        setUfCarregamento(data.uf_carregamento || '');
        setMunicipioCarregamento(data.municipio_carregamento_nome && data.municipio_carregamento_codigo
          ? `${data.municipio_carregamento_nome}|${data.municipio_carregamento_codigo}` : '');
        setVeiculoTracaoId(data.veiculo_tracao_id || '');
        setReboquesIds((data.reboques || []).map(r => r.veiculo_id));
        setCondutoresIds((data.condutores || []).map(c => c.condutor_id));
        setPercurso((data.percurso || []).map(p => p.uf));
        setInformacoesAdicionais(data.informacoes_adicionais || '');
        const mapa = {};
        for (const d of (data.documentos || [])) {
          mapa[d.documento_fiscal_id || d.chave_acesso] = {
            documento_fiscal_id: d.documento_fiscal_id, tipo: d.tipo, chave_acesso: d.chave_acesso,
            municipio_descarga_codigo: d.municipio_descarga_codigo, municipio_descarga_nome: d.municipio_descarga_nome,
            cliente_uf: d.uf_descarga, peso_kg: d.peso_kg, valor: d.valor,
            numero: d.numero, cliente_nome: d.cliente_nome,
          };
        }
        setSelecionadas(mapa);
      } catch {
        setErro('Erro ao carregar o MDF-e.');
      } finally { setCarregando(false); }
    })();
  }, [mdfeId]);

  const listaSelecionadas = useMemo(() => Object.values(selecionadas), [selecionadas]);

  const totais = useMemo(() => listaSelecionadas.reduce((acc, d) => ({
    peso: acc.peso + (Number(d.peso_kg) || 0),
    valor: acc.valor + (Number(d.valor) || 0),
  }), { peso: 0, valor: 0 }), [listaSelecionadas]);

  const municipiosDescargaUnicos = useMemo(() => {
    const nomes = new Set(listaSelecionadas.map(d => d.municipio_descarga_nome).filter(Boolean));
    return [...nomes];
  }, [listaSelecionadas]);

  // UF de descarregamento sugerida = UF mais frequente entre as NF-e
  // selecionadas — o usuário pode mudar (percurso cobre o caso de passar
  // por mais estados).
  const ufDescarregamentoSugerida = useMemo(() => {
    const cont = {};
    for (const d of listaSelecionadas) if (d.cliente_uf) cont[d.cliente_uf] = (cont[d.cliente_uf] || 0) + 1;
    const [uf] = Object.entries(cont).sort((a, b) => b[1] - a[1])[0] || [];
    return uf || '';
  }, [listaSelecionadas]);
  const [ufDescarregamento, setUfDescarregamento] = useState('');
  useEffect(() => { if (!ufDescarregamento && ufDescarregamentoSugerida) setUfDescarregamento(ufDescarregamentoSugerida); }, [ufDescarregamentoSugerida, ufDescarregamento]);

  const toggleNfe = (nf) => {
    setSelecionadas(prev => {
      const chave = nf.documento_fiscal_id;
      if (prev[chave]) { const n = { ...prev }; delete n[chave]; return n; }
      return { ...prev, [chave]: nf };
    });
  };

  const toggleReboque = (id) => {
    setReboquesIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleCondutor = (id) => {
    setCondutoresIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const adicionarPercurso = () => {
    if (!percursoNovo) return;
    setPercurso(prev => [...prev, percursoNovo]);
    setPercursoNovo('');
  };
  const removerPercurso = (i) => setPercurso(prev => prev.filter((_, idx) => idx !== i));

  const montarPayload = () => {
    const [nome, codigo] = (municipioCarregamento || '').split('|');
    return {
      uf_carregamento: ufCarregamento,
      municipio_carregamento_codigo: codigo || '',
      municipio_carregamento_nome: nome || '',
      uf_descarregamento: ufDescarregamento,
      veiculo_tracao_id: veiculoTracaoId ? parseInt(veiculoTracaoId) : null,
      reboques: reboquesIds,
      condutores: condutoresIds,
      percurso,
      informacoes_adicionais: informacoesAdicionais || null,
      documentos: listaSelecionadas,
    };
  };

  const salvar = async () => {
    if (listaSelecionadas.length === 0) { setErro('Selecione ao menos uma NF-e.'); return; }
    if (!ufCarregamento || !municipioCarregamento) { setErro('Informe UF e município de carregamento.'); return; }
    setErro(''); setSalvando(true); setResultadoValidacao(null);
    try {
      const payload = montarPayload();
      if (mdfeId) await api.put(`/fiscal/mdfe/${mdfeId}`, payload);
      else { const { data } = await api.post('/fiscal/mdfe', payload); onSalvo(data.id, false); return; }
      onSalvo(mdfeId, false);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  const salvarEValidar = async () => {
    if (listaSelecionadas.length === 0) { setErro('Selecione ao menos uma NF-e.'); return; }
    if (!ufCarregamento || !municipioCarregamento) { setErro('Informe UF e município de carregamento.'); return; }
    setErro(''); setSalvando(true); setResultadoValidacao(null);
    try {
      const payload = montarPayload();
      let id = mdfeId;
      if (id) await api.put(`/fiscal/mdfe/${id}`, payload);
      else { const { data } = await api.post('/fiscal/mdfe', payload); id = data.id; }
      const { data: resultado } = await api.post(`/fiscal/mdfe/${id}/validar`);
      setResultadoValidacao(resultado);
      onSalvo(id, resultado.ok);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar/validar.');
    } finally { setSalvando(false); }
  };

  const podeEditar = ['digitacao', 'rejeitado', 'validado'].includes(statusAtual);

  if (carregando) {
    return (
      <div className={s.overlay}><div className={s.modal} style={{ maxWidth: 400 }}>
        <div className={s.modalBody} style={{ padding: 30, textAlign: 'center' }}>Carregando...</div>
      </div></div>
    );
  }

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ maxWidth: 980, maxHeight: '92vh' }}>
        <div className={s.modalTitle}>🚚 {mdfeId ? `Editar MDF-e` : 'Novo MDF-e'} {!podeEditar && '(somente leitura — já transmitido)'}</div>
        <div className={s.modalBody} style={{ overflowY: 'auto' }}>
          {erro && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: 8, borderRadius: 4, marginBottom: 10, fontSize: 13 }}>{erro}</div>}

          {resultadoValidacao && (
            <div style={{
              background: resultadoValidacao.ok ? '#dcfce7' : '#fef3c7',
              color: resultadoValidacao.ok ? '#166534' : '#92400e',
              padding: 10, borderRadius: 4, marginBottom: 10, fontSize: 13,
            }}>
              {resultadoValidacao.ok ? '✔ MDF-e validado — pronto para transmissão (Etapa 4).' : (
                <>
                  <strong>Não foi possível validar o MDF-e:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {resultadoValidacao.problemas.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* 1 — Documentos */}
          <fieldset style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 6, marginBottom: 12, padding: 10 }} disabled={!podeEditar}>
            <legend style={{ fontSize: 12, fontWeight: 'bold' }}>1. NF-e a transportar ({listaSelecionadas.length} selecionada{listaSelecionadas.length !== 1 ? 's' : ''})</legend>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border,#e2e8f0)', borderRadius: 4 }}>
              <table className={s.modalTabela}>
                <thead><tr>
                  <th style={{ width: 30 }}></th><th>Número</th><th>Cliente</th><th>Destino</th>
                  <th style={{ textAlign: 'right' }}>Valor</th><th style={{ textAlign: 'right' }}>Peso (kg)</th>
                </tr></thead>
                <tbody>
                  {[...listaSelecionadas.filter(d => !nfeDisponiveis.find(n => n.documento_fiscal_id === d.documento_fiscal_id)), ...nfeDisponiveis].map(nf => (
                    <tr key={nf.documento_fiscal_id} onClick={() => podeEditar && toggleNfe(nf)} style={{ cursor: podeEditar ? 'pointer' : 'default' }}
                      className={selecionadas[nf.documento_fiscal_id] ? s.selRow : ''}>
                      <td><input type="checkbox" readOnly checked={!!selecionadas[nf.documento_fiscal_id]} /></td>
                      <td>{nf.numero}{nf.serie ? `/${nf.serie}` : ''}</td>
                      <td>{nf.cliente_nome || '—'}</td>
                      <td>{nf.municipio_descarga_nome || nf.cliente_cidade || '—'}/{nf.cliente_uf || '—'}
                        {!nf.municipio_descarga_codigo && <span style={{ color: '#b91c1c' }} title="Cidade do cliente não encontrada na tabela IBGE"> ⚠</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(nf.valor)}</td>
                      <td style={{ textAlign: 'right' }}>{Number(nf.peso_kg || 0).toLocaleString('pt-BR')}{!nf.peso_kg && <span style={{ color: '#b91c1c' }} title="Sem peso cadastrado no produto"> ⚠</span>}</td>
                    </tr>
                  ))}
                  {nfeDisponiveis.length === 0 && listaSelecionadas.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 10 }}>Nenhuma NF-e autorizada disponível.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, marginTop: 6, display: 'flex', gap: 16 }}>
              <span>Municípios de descarga: <strong>{municipiosDescargaUnicos.join(', ') || '—'}</strong></span>
              <span>Peso total: <strong>{totais.peso.toLocaleString('pt-BR')} kg</strong></span>
              <span>Valor total: <strong>{fmtMoeda(totais.valor)}</strong></span>
            </div>
          </fieldset>

          {/* 2 — Carregamento / Descarregamento */}
          <fieldset style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 6, marginBottom: 12, padding: 10 }} disabled={!podeEditar}>
            <legend style={{ fontSize: 12, fontWeight: 'bold' }}>2. Carregamento e descarregamento</legend>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12 }}>UF de carregamento *<br />
                <select className={s.inputErp} value={ufCarregamento} onChange={e => { setUfCarregamento(e.target.value); setMunicipioCarregamento(''); }}>
                  <option value="">—</option>{UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12, flex: 1, minWidth: 220 }}>Município de carregamento *<br />
                <select className={s.inputErp} style={{ width: '100%' }} value={municipioCarregamento} onChange={e => setMunicipioCarregamento(e.target.value)} disabled={!ufCarregamento}>
                  <option value="">—</option>
                  {municipiosCarregamento.map(m => <option key={m.codigo} value={`${m.nome}|${m.codigo}`}>{m.nome}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12 }}>UF de descarregamento (final) *<br />
                <select className={s.inputErp} value={ufDescarregamento} onChange={e => setUfDescarregamento(e.target.value)}>
                  <option value="">—</option>{UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </label>
            </div>
          </fieldset>

          {/* 3 — Veículo / Reboques / Condutores */}
          <fieldset style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 6, marginBottom: 12, padding: 10 }} disabled={!podeEditar}>
            <legend style={{ fontSize: 12, fontWeight: 'bold' }}>3. Veículo e condutor(es)</legend>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12 }}>Veículo de tração *<br />
                <select className={s.inputErp} value={veiculoTracaoId} onChange={e => setVeiculoTracaoId(e.target.value)}>
                  <option value="">—</option>
                  {veiculos.filter(v => !v.eh_reboque).map(v => <option key={v.id} value={v.id}>{v.placa}</option>)}
                </select>
              </label>
              <div style={{ fontSize: 12 }}>
                Reboques<br />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: 300 }}>
                  {veiculos.filter(v => v.eh_reboque).map(v => (
                    <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="checkbox" checked={reboquesIds.includes(v.id)} onChange={() => toggleReboque(v.id)} /> {v.placa}
                    </label>
                  ))}
                  {veiculos.filter(v => v.eh_reboque).length === 0 && <span style={{ color: '#888' }}>— nenhum reboque cadastrado —</span>}
                </div>
              </div>
              <div style={{ fontSize: 12, flex: 1, minWidth: 220 }}>
                Condutor(es) *<br />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {condutoresLista.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="checkbox" checked={condutoresIds.includes(c.id)} onChange={() => toggleCondutor(c.id)} /> {c.nome}
                    </label>
                  ))}
                  {condutoresLista.length === 0 && <span style={{ color: '#888' }}>— nenhum condutor cadastrado —</span>}
                </div>
              </div>
            </div>
          </fieldset>

          {/* 4 — Percurso */}
          <fieldset style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 6, marginBottom: 12, padding: 10 }} disabled={!podeEditar}>
            <legend style={{ fontSize: 12, fontWeight: 'bold' }}>4. Percurso (UFs intermediárias, só se necessário)</legend>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {percurso.map((uf, i) => (
                <span key={i} style={{ background: 'var(--content-bg,#f1f5f9)', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
                  {i + 1}. {uf} {podeEditar && <button onClick={() => removerPercurso(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b91c1c' }}>✕</button>}
                </span>
              ))}
              {podeEditar && (
                <>
                  <select className={s.inputErp} style={{ width: 80 }} value={percursoNovo} onChange={e => setPercursoNovo(e.target.value)}>
                    <option value="">UF</option>{UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                  <button className={s.btnErp} onClick={adicionarPercurso} disabled={!percursoNovo}>+ Adicionar</button>
                </>
              )}
            </div>
          </fieldset>

          {/* 5 — Informações adicionais */}
          <fieldset style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 6, padding: 10 }} disabled={!podeEditar}>
            <legend style={{ fontSize: 12, fontWeight: 'bold' }}>5. Informações adicionais</legend>
            <textarea className={s.inputErp} style={{ width: '100%', minHeight: 50 }} value={informacoesAdicionais}
              onChange={e => setInformacoesAdicionais(e.target.value)} placeholder="Observações do manifesto (opcional)" />
          </fieldset>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
          {podeEditar && (
            <>
              <button className={s.btnErp} onClick={salvar} disabled={salvando}>
                {salvando ? '...' : '💾 Salvar rascunho'}
              </button>
              <button className={s.btnErpPrimario} onClick={salvarEValidar} disabled={salvando}>
                {salvando ? 'Validando...' : '✔ Salvar e Validar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
